import React, { useEffect, useState, useRef, RefObject } from 'react';
import './Select.scss';

export type SelectProps = {
  id: string
  label: string;
  value: string;
  error?: string | boolean;
  span?: string | undefined;
  onChange(value: string): void;
  onBlur?(): void;
  options: string[];
  optionLabels?: {[key: string]: string};
  ariaInvalid?: boolean;
  describedById?: string;
};


const scrollToItem = (listRef: RefObject<HTMLDivElement>, index: number) => {
  if (listRef.current) {
    const item = listRef.current.children[index];
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }
};

export const Select: React.FunctionComponent<SelectProps> = (props: SelectProps): JSX.Element => {
  const {
    onChange,
    options,
    optionLabels,
    onBlur,
    error,
    value,
    label,
    span,
    id,
    ariaInvalid,
    describedById,
  } = props;

  const selectClassName = `
    text-sm outline w-full leading-tight
    input-outline bg-white
    ${error ? ' border-red-600' : ''}
  `;

  const labelClassName = 'block font-bold mt-3 text-sm mb-2';

  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectRef: RefObject<HTMLDivElement> | null = useRef<HTMLDivElement>(null);
  const buttonRef: RefObject<HTMLButtonElement> | null = useRef<HTMLButtonElement>(null);
  const optionsRef: RefObject<HTMLDivElement> | null = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<number>(0);
  const searchedValueRef = useRef<string>('');

  const getSelectedIndex = (): number => {
    const idx = options.findIndex(o => o === value);
    return idx >= 0 ? idx : 0;
  };

  const onKeyUpEventHandler = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (!showDropDown) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setShowDropDown(true);
        setHighlightedIndex(getSelectedIndex());
      }
      return;
    }

    switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < options.length - 1 ? prev + 1 : 0
      );
      break;
    case 'ArrowUp':
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : options.length - 1
      );
      break;
    case 'Home':
      e.preventDefault();
      setHighlightedIndex(0);
      break;
    case 'End':
      e.preventDefault();
      setHighlightedIndex(options.length - 1);
      break;
    case 'Escape':
      e.preventDefault();
      setShowDropDown(false);
      setHighlightedIndex(-1);
      break;
    case 'Enter':
      e.preventDefault();
      if (highlightedIndex >= 0) {
        selectItem(options[highlightedIndex]);
        setShowDropDown(false);
        setHighlightedIndex(-1);
      }
      break;
    case 'Tab':
      setShowDropDown(false);
      setHighlightedIndex(-1);
      onBlur?.();
      break;
    default:
      if (e.key.length === 1) {
        e.preventDefault();
        window.clearTimeout(searchTimeoutRef.current);
        const currentValue = searchedValueRef.current;
        searchedValueRef.current = currentValue + e.key.toLowerCase();

        const matchIndex = options.findIndex(option =>
          option.toLowerCase().startsWith(searchedValueRef.current)
        );
        if (matchIndex !== -1) {
          setHighlightedIndex(matchIndex);
          scrollToItem(optionsRef, matchIndex);
        }
        searchTimeoutRef.current = window.setTimeout(() => {
          searchedValueRef.current = '';
        }, 1000);
      }
    }
  };

  const toggleDropDown = () => {
    setShowDropDown(prev => !prev);
    if (!showDropDown) {
      setHighlightedIndex(getSelectedIndex());
    } else {
      setHighlightedIndex(-1);
    }
  };

  const selectItem = (item: string) => {
    onChange(item);
    setShowDropDown(false);
    setHighlightedIndex(-1);
    buttonRef.current?.focus();
  };

  const handleOutsideClick = (e: Event) => {
    const target = e.target as HTMLElement;
    if (selectRef?.current?.contains(target)) {
      return;
    }
    setShowDropDown(false);
    setHighlightedIndex(-1);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && showDropDown) {
      scrollToItem(optionsRef, highlightedIndex);
    }
  }, [highlightedIndex, showDropDown]);

  return (
    <>
      <div className='flex'>
        <label className={labelClassName} title={label} htmlFor={id} id={`${id}-label`}>
          <span className='mr-1'>{label}</span>
          <span className='mt-3 text-sm mb-2 font-normal'>{span}</span>
        </label>
      </div>
      <div
        ref={selectRef}
        className={`custom-select ${selectClassName}`}
      >
        <div>
          <div
            className='custom-select--option'
            onClick={toggleDropDown}
          >
            <button
              ref={buttonRef}
              type='button'
              id={id}
              role='combobox'
              className='custom-select__selected-item input-outline text-ellipsis px-3 py-2 text-left'
              onBlur={onBlur}
              onKeyDown={onKeyUpEventHandler}
              tabIndex={0}
              aria-labelledby={`${id}-label`}
              aria-haspopup='listbox'
              aria-expanded={showDropDown}
              aria-controls={`${id}-listbox`}
              aria-invalid={ariaInvalid}
              aria-describedby={describedById}
              aria-activedescendant={showDropDown && highlightedIndex >= 0 ? `${id}-option-${highlightedIndex}` : undefined}
            >
              {optionLabels?.[value] || value || 'Select an option'}
            </button>
            <div className={`custom-select__arrow ${showDropDown ? 'custom-select__arrow--open' : ''}`} />
          </div>
          {showDropDown &&
            <div
              role='listbox'
              id={`${id}-listbox`}
              className={`custom-select__options outline`}
              ref={optionsRef}
              aria-labelledby={`${id}-label`}
            >
              {options.map((option, i) => {
                const isActive = highlightedIndex === i;
                return (
                  <button
                    type='button'
                    key={i}
                    id={`${id}-option-${i}`}
                    onClick={() => selectItem(option)}
                    className={`option px-3 ${isActive ? 'selected' : ''}`}
                    role='option'
                    aria-selected={isActive ? true : undefined}
                  >
                    {optionLabels?.[option] || option || 'Select an option'}
                  </button>
                );})}
            </div>
          }
        </div>
      </div>
      {error && (typeof error === 'boolean') && <span className='text-red-600 text-xs py-4'>{error}</span>}
    </>
  );
};

export default Select;
